// components/CustomerSelector.tsx - Updated with selection management
import React, { useMemo, useCallback, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    SelectChangeEvent,
    Alert,
    Typography
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setSelectedCustomer } from '@/store/slices/selectionSlice';
import {
    selectCustomers,
    selectCustomerLoading,
    selectCustomerError,
    selectSelectedCustomerId,
} from '@/store/selectors';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { use } from 'i18next';
import { useLocation } from 'react-router-dom';

interface CustomerSelectorProps {
    label?: string;
    fullWidth?: boolean;
}

export const CustomerSelector: React.FC<CustomerSelectorProps> = React.memo(({
    label = 'Select Customer',
    fullWidth = true,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();

    const customers = useAppSelector(selectCustomers);
    const loading = useAppSelector(selectCustomerLoading);
    const error = useAppSelector(selectCustomerError);
    const selectedType = useAppSelector((state: RootState) => state.selection.selectedType);
    const selectedCustomerId = useAppSelector(selectSelectedCustomerId);
    const { data: userData } = useAppSelector((state: RootState) => state.user);
    const isDeviceDetailPage = pathname.startsWith("/devices/");
    const isSearchResultsPage = pathname.startsWith("/search-results/");
    const isWalkdownDetailPage = /^\/walkdowns\/(?!qr-codes)[^/]+$/.test(pathname);
    const isTurnAroundDetailPage = /^\/turnarounds\/[^/]+$/.test(pathname);
    const freezeForm = isDeviceDetailPage || isSearchResultsPage || isWalkdownDetailPage || isTurnAroundDetailPage;

    // Reconstruct composite value for the Select
    const selectedKey = selectedType && selectedCustomerId ? `${selectedType}-${selectedCustomerId}` : '';

    const customerList = useMemo(() => {
        console.log('Customers in CustomerSelector:', customers);
        if (!customers) return [];
        return Array.isArray(customers.customers) ? customers.customers : [customers];
    }, [customers]);

    const siteGroupList = useMemo(() => {
        if (!customers) return [];
        return Array.isArray(customers.site_groups) ? customers.site_groups : [customers];
    }, [customers]);

    const handleChange = useCallback((event: SelectChangeEvent<string>) => {
        const selectedId = event.target.value || null;
        console.log('CUSTOMER SELECTOR: Selected Customer ID', selectedId);
        const ctype = selectedId?.includes('-') ? selectedId.split('-')[0] : '';
        const cid = selectedId?.includes('-') ? selectedId.split('-')[1] : selectedId;
        console.log('CUSTOMER SELECTOR: Parsed Type and ID', ctype, cid);
        dispatch(setSelectedCustomer({
            userid: userData?.id || '',
            type: ctype,
            id: cid,
        }));
    }, [userData, dispatch]);

    // Show loading state
    // if (loading) {
    //     return (
    //         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2 }}>
    //             <CircularProgress size={24} />
    //             <span>Loading customers...</span>
    //         </Box>
    //     );
    // }

    // // Show error state
    // if (error) {
    //     return (
    //         <Alert severity="error" sx={{ marginY: 2 }}>
    //             Error loading customers: {error}
    //         </Alert>
    //     );
    // }

    useEffect(() => {
        if (!selectedCustomerId) {
            if (customerList.length > 0) {
                const defaultCustomer = customerList.find((x) => x.default === true);
                console.log('CustomerSelector: auto-selecting default customer', defaultCustomer);
                dispatch(setSelectedCustomer({
                    userid: userData?.id || '',
                    type: 'customers',
                    id: defaultCustomer?.id,
                }));
            } else if (siteGroupList.length > 0) {
                const defaultGroup = siteGroupList.find((x) => x.default === true);
                console.log('CustomerSelector: auto-selecting default site group', defaultGroup);
                dispatch(setSelectedCustomer({
                    userid: userData?.id || '',
                    type: 'site_groups',
                    id: defaultGroup?.id,
                }));
            }
        }
    }, [selectedCustomerId, customerList, siteGroupList, userData, dispatch]);

    return (
        <Select
            fullWidth={fullWidth}
            label={label}
            value={selectedKey}
            displayEmpty
            onChange={handleChange}
            disabled={freezeForm}
            variant="filled"
            size="small"
            sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '48px !important',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  '& .MuiFilledInput-root': {
                    '&:before, &:after': {
                      display: 'none',
                    },
                  },
                }}

            renderValue={(selected) => {
                    if (!selected) {
                    return (
                        <Typography className="csgf-placeholder">
                        {t("selectCustomerOrSiteGroup", "Select Customer or Site Group")}
                        </Typography>
                    );
                    }
                    const [ctype, cid] = String(selected).split('-');
                    let labelText: string | undefined;

                    if (ctype === 'customers') {
                        labelText = customerList.find((c: any) => String(c.id) === cid)?.name;
                    } else if (ctype === 'site_groups') {
                        labelText = siteGroupList.find((g: any) => String(g.id) === cid)?.name;
                    }

                    return (
                    <Typography className="csgf-selected">
                        {labelText ?? t("selectCustomerOrSiteGroup", "Select Customer or Site Group")}
                    </Typography>
                    );
                }}

        >
            {
                // customerList.length > 0 && customerList.map((customer) => (
                //     <MenuItem key={customer.id} value={customer.id}>
                //         {customer.name}
                //     </MenuItem>
                // ))
                customerList?.length > 0 && [
                  <MenuItem key="customers-label" disabled>
                    {t("customers", "Customers")}
                  </MenuItem>,
                  ...[...customerList]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((customer) => (
                      <MenuItem
                        key={`customers-${customer.id}`}
                        value={`customers-${customer.id}`}
                      >
                        {customer.name}
                      </MenuItem>
                    )),
                ]

            }
            {
                siteGroupList?.length > 0 && [
                  <MenuItem key="site-groups-label" disabled>
                    {t("siteGroups", "Site Groups")}
                  </MenuItem>,
                  ...siteGroupList.map((group) => (
                    <MenuItem
                      key={`site_groups-${group.id}`}
                      value={`site_groups-${group.id}`}
                    >
                      {group.name}
                    </MenuItem>
                  )),
                ]
            }

        </Select>
        // <FormControl fullWidth={fullWidth} sx={{ marginY: 2 }}>
        //     <InputLabel id="customer-select-label">{label}</InputLabel>
        //     <Select
        //         labelId="customer-select-label"
        //         id="customer-select"
        //         value={selectedCustomerId || ''}
        //         label={label}
        //         onChange={handleChange}
        //     >
        //         {customerList.map((customer) => (
        //             <MenuItem key={customer.id} value={customer.id}>
        //                 {customer.name}
        //             </MenuItem>
        //         ))}
        //     </Select>
        // </FormControl>
    );
});

CustomerSelector.displayName = 'CustomerSelector';
export default CustomerSelector;